import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { animated, useSpring } from '@react-spring/web';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { proxyUrl } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { RewardUnlockedAnimation } from '@sorare/core/src/components/rewards/RewardUnlockedAnimation';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';

import { Shape } from '../Shape';
import { useWheelReward } from '../utils';
import type {
  Reward_probabilisticBundleSlotConfig,
  Reward_probabilisticBundleSlotItemInterface,
} from './__generated__/index.graphql';

const Wrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: var(--100vw);
  height: 100vh;
  display: grid;
  align-content: center;
  justify-content: center;
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: var(--100vw);
    height: 400vh;
    background-color: var(--c-nl-700);
    z-index: -1;
  }
`;

type Props = {
  item: Reward_probabilisticBundleSlotItemInterface;
  config: Reward_probabilisticBundleSlotConfig;
  rarity: Rarity;
  onClose: () => void;
};

export const Reward = ({ item, config, rarity, onClose }: Props) => {
  const {
    description: shortDescription,
    longDescription,
    illustration,
    rewardName,
    amount,
  } = useWheelReward(config);
  const title = rewardName || longDescription || shortDescription;
  const card = isType(item, 'ProbabilisticBundleSlotCardItem')
    ? item.anyCard
    : null;

  const [showContinue, setShowContinue] = useState(false);

  const spring = useSpring({
    from: { opacity: 0 },
    to: { opacity: showContinue ? 1 : 0 },
    config: { duration: 500 },
  });

  return (
    <Wrapper onClick={onClose}>
      <Vertical center gap={3}>
        <Shape
          config={config}
          rarity={rarity}
          style={{
            viewTransitionName: 'wheel-reward',
            maxWidth: 'min(350px, var(--100vw))',
          }}
        >
          <Vertical
            style={{
              height: 'clamp(400px, 80vh, 600px)',
              padding: 'var(--double-unit)',
              position: 'relative',
              minWidth: 'min(300px, calc(0.9 * var(--100vw)))',
            }}
            gap={4}
            center
          >
            <RewardUnlockedAnimation
              imgUrls={[
                card?.pictureUrl ??
                  proxyUrl(illustration, {
                    cropWidth: 80,
                  }),
              ]}
              title={title}
              subtitle=""
              amount={amount}
              onAnimationEnd={() => setShowContinue(true)}
              style={
                {
                  flex: 1,
                  padding: 0,
                  '--image-size': '80%',
                } as React.CSSProperties
              }
            />

            <animated.div style={spring}>
              <Button color="primary" size="large" onClick={onClose}>
                <FormattedMessage {...glossary.continue} />
              </Button>
            </animated.div>
          </Vertical>
        </Shape>
      </Vertical>
    </Wrapper>
  );
};

Reward.fragments = {
  probabilisticBundleSlotItemInterface: gql`
    fragment Reward_probabilisticBundleSlotItemInterface on ProbabilisticBundleSlotItemInterface {
      id
      ... on ProbabilisticBundleSlotCardItem {
        id
        anyCard {
          slug
          pictureUrl
        }
      }
    }
  ` as TypedDocumentNode<Reward_probabilisticBundleSlotItemInterface>,
  probabilisticBundleSlotConfig: gql`
    fragment Reward_probabilisticBundleSlotConfig on ProbabilisticBundleSlotConfig {
      id
      ...Shape_probabilisticBundleSlotConfig
      ...useWheelReward_probabilisticBundleSlotConfig
    }
    ${Shape.fragments.ProbabilisticBundleSlotConfig}
    ${useWheelReward.fragments.probabilisticBundleSlotConfig}
  ` as TypedDocumentNode<Reward_probabilisticBundleSlotConfig>,
};
